import _ from 'lodash';
import { useMemo } from 'react';

import { CHAIN_ID } from '@/constants/chain';

import { WrappedInstrument } from '@/entities/WrappedInstrument';
import { useAppSelector } from '@/hooks';
import { IMetaInstrument } from '@/types/pair';

import { QUERY_KEYS } from '@/constants/query';
import { SOCKET_EVENTS, SOCKET_METHODS, SOCKET_TYPES } from '@/entities/SocketService';
import { WrappedPair } from '@/entities/WrappedPair';
import { queryClient } from '@/global';
import useSocket from '@/hooks/useSocket';
import { useUserAddr } from '@/hooks/web3/useChain';
import { IFuturesOrderBook } from '@/types/futures';
import { transformData } from '@/utils/chart';
import { transformToMetaFutures, transformToMetaPair } from '@/utils/transform/pair';
import { getPairId } from '@/utils/transform/transformId';
import { FundingChartInterval, KlineInterval } from '@synfutures/sdks-perp-datasource';
import { useDebounceEffect } from 'ahooks';
import { useTokenPrices } from '../global/query';
import { transformMarketInfo } from '../market/api';
import { useMarketList } from '../market/hooks';
import { useFetchMarketPairInfo } from '../market/query';
import { useTradeStore } from '../trade/store';
import { transformFuturesInstrument, transformFuturesPairOrderBook } from './api';
import {
  useFetchFuturesDepthChart,
  useFetchFuturesFundingChart,
  useFetchFuturesInstrument,
  useFetchFuturesKlineChart,
  useFetchFuturesOrderbook,
} from './query';
import { selectChainMetaFutures } from './slice';

export function useMetaFuturesMap(chainId: CHAIN_ID | undefined): { [instrumentId: string]: IMetaInstrument } {
  const chainMetaInstruments = useAppSelector(selectChainMetaFutures(chainId));

  return useMemo(() => {
    return chainMetaInstruments || {};
  }, [chainMetaInstruments]);
}

export function useWrappedInstrument(
  chainId: CHAIN_ID | undefined,
  instrumentId: string | undefined,
): WrappedInstrument | undefined {
  const instrumentMap = useMetaFuturesMap(chainId);
  return useMemo(() => {
    if (!chainId || !instrumentId) return undefined;
    const origin = instrumentMap[instrumentId];
    if (origin) {
      const futures = WrappedInstrument.getInstance(origin.id, chainId);
      if (!futures) {
        return WrappedInstrument.wrapInstance({ metaFutures: origin, chainId });
      } else {
        futures.fillField(origin);
      }
      return futures;
    }
    return undefined;
  }, [chainId, instrumentMap, instrumentId]);
}

export function useWrappedInstrumentMap(chainId: CHAIN_ID | undefined): { [instrumentId: string]: WrappedInstrument } {
  const instrumentsMap = useMetaFuturesMap(chainId);
  const { data: tokenPriceMap } = useTokenPrices({ chainId });

  return useMemo(() => {
    if (!chainId) return {};
    return _.mapValues(instrumentsMap, (origin) => {
      const futures = WrappedInstrument.getInstance(origin.id, chainId);
      if (!futures) {
        return WrappedInstrument.wrapInstance({ metaFutures: origin, chainId });
      } else {
        futures.fillField(origin);
      }
      if (tokenPriceMap) {
        const quote = futures.quoteToken;
        const quotePriceInfo = tokenPriceMap[quote.address];
        if (quotePriceInfo) {
          futures.setQuotePrice(quotePriceInfo);
        }
      }
      return futures;
    });
  }, [chainId, instrumentsMap, tokenPriceMap]);
}

export const useMarketPairInfo = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) => {
  const { marketPairList } = useMarketList(chainId);
  const { data: pairInfo } = useFetchMarketPairInfo({ chainId, instrumentAddr, expiry });

  return useMemo(() => {
    if (pairInfo) {
      return pairInfo;
    }
    if (marketPairList && instrumentAddr && expiry) {
      const pair = marketPairList.find((p) => p.instrumentAddr === instrumentAddr && p.expiry === expiry);
      return pair;
    }
    return undefined;
  }, [expiry, instrumentAddr, marketPairList, pairInfo]);
};

export const useIsFetchedPairInfo = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) => {
  const { isFetched } = useFetchMarketPairInfo({ chainId, instrumentAddr, expiry });

  return useMemo(() => {
    if (isFetched) {
      return isFetched;
    }

    return false;
  }, [isFetched]);
};

export function useMarketWrappedInstrument(chainId: number | undefined, instrumentAddr: string | undefined) {
  const { data: marketInstrument } = useFetchFuturesInstrument(chainId, instrumentAddr);
  return useMemo(() => {
    if (marketInstrument && chainId) {
      const metaFutures = transformToMetaFutures(marketInstrument, chainId);
      let wrappedInstrument = WrappedInstrument.getInstance(metaFutures.id, chainId);
      if (wrappedInstrument) wrappedInstrument.fillField(metaFutures);
      else wrappedInstrument = WrappedInstrument.wrapInstance({ metaFutures: metaFutures, chainId: chainId });
      if (wrappedInstrument && marketInstrument?.amms) {
        marketInstrument.amms.forEach((amm) => {
          const pairId = getPairId(amm.instrumentAddr, amm.expiry);
          let pair = WrappedPair.getInstance(pairId, chainId);
          const metaPair = transformToMetaPair({ amm, futures: marketInstrument });
          if (!pair) {
            if (wrappedInstrument) {
              pair = WrappedPair.wrapInstance({
                metaPair: metaPair,
                rootInstrument: wrappedInstrument,
              });
              pair && wrappedInstrument?.connectPair(pair.expiry, pair);
            }
          } else {
            pair.fillField(metaPair);
          }
        });
      }
      return wrappedInstrument;
    }
  }, [chainId, marketInstrument]);
}

export const useIsFetchedInstrument = (chainId: number | undefined, instrumentAddr: string | undefined) => {
  const { isFetched } = useFetchFuturesInstrument(chainId, instrumentAddr);

  return isFetched;
};

/**
 * A hook that subscribes to market pair info changes via websocket
 * Listens for orderBook, depth, and instrument updates and invalidates corresponding queries
 *
 * @param chainId - The blockchain network ID
 * @param instrumentAddr - The address of the futures instrument
 */
// export function useWatchMarketPairInfoChange(
//   chainId: number | undefined,
//   instrumentAddr: string | undefined,
//   expiry: number | undefined,
// ): void {
//   const { socket } = useSocket();
//   const stepRatio = useStepRatio(chainId);

//   useEffect(() => {
//     if (!socket || !chainId || !instrumentAddr || !expiry) return;
//     const param = {
//       chainId: chainId,
//       address: instrumentAddr,
//       expiry: expiry,
//       type: SOCKET_TYPES.PAIR_INFO,
//     };
//     socket.emit({
//       id: Date.now(),
//       method: SOCKET_METHODS.SUBSCRIBE,
//       params: param,
//     });

//     socket.on(SOCKET_TYPES.PAIR_INFO, (data) => {
//       console.log('event', SOCKET_TYPES.PAIR_INFO, data.type, data);
//       if (data.type === 'orderBook') {
//         queryClient.invalidateQueries({
//           queryKey: QUERY_KEYS.FUTURES.ORDER_BOOK(chainId, instrumentAddr, expiry, stepRatio),
//         });
//       } else if (data.type === 'depth') {
//         queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FUTURES.DEPTH_CHARTS(chainId, instrumentAddr, expiry) });
//       } else if (data.type === 'instrument') {
//         setTimeout(() => {
//           queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FUTURES.INSTRUMENT(chainId, instrumentAddr) });
//           queryClient.invalidateQueries({
//             queryKey: QUERY_KEYS.FUTURES.PAIR_INFO(chainId, instrumentAddr, expiry),
//           });
//         }, 3000);
//       } else if (data.type === 'info') {
//         queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FUTURES.INSTRUMENT(chainId, instrumentAddr) });
//         queryClient.invalidateQueries({
//           queryKey: QUERY_KEYS.FUTURES.PAIR_INFO(chainId, instrumentAddr, expiry),
//         });
//       }
//     });

//     return () => {
//       socket.off(SOCKET_TYPES.PAIR_INFO);
//       socket.emit({ id: Date.now(), method: SOCKET_METHODS.UNSUBSCRIBE, params: param });
//     };
//   }, [chainId, expiry, instrumentAddr, socket, stepRatio]);
// }

export function useWatchOrderbookChange(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
): void {
  const { socket } = useSocket();
  const { stepRatio } = useTradeStore();
  const userAddr = useUserAddr();
  useDebounceEffect(
    () => {
      if (!socket || !chainId || !instrumentAddr || !expiry) return;
      const param = {
        chainId: chainId,
        instrument: instrumentAddr,
        expiry: expiry,
        type: SOCKET_TYPES.ORDEBOOK,
      };
      const subscribeId = Date.now();
      socket.emit({
        id: subscribeId,
        method: SOCKET_METHODS.SUBSCRIBE,
        params: param,
      });

      socket.on(SOCKET_EVENTS.ORDER_BOOK_CHANGED, (data) => {
        console.record('event', SOCKET_EVENTS.ORDER_BOOK_CHANGED, data.type, data);

        const newOrderBook = transformFuturesPairOrderBook(data);
        queryClient.setQueryData(
          QUERY_KEYS.FUTURES.ORDER_BOOK(chainId, instrumentAddr, expiry, stepRatio),
          newOrderBook,
        );
      });

      return () => {
        socket.off(SOCKET_EVENTS.ORDER_BOOK_CHANGED);
        socket.emit({ id: subscribeId, method: SOCKET_METHODS.UNSUBSCRIBE, params: param });
      };
    },
    [chainId, expiry, instrumentAddr, socket, stepRatio, userAddr],
    {
      wait: 200,
    },
  );
}

export function useWatchInstrumentChange(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
): void {
  const { socket } = useSocket();

  useDebounceEffect(
    () => {
      if (!socket || !chainId || !instrumentAddr || !expiry) return;
      const param = {
        chainId: chainId,
        instrument: instrumentAddr,
        expiry: expiry,
        type: SOCKET_TYPES.INSTRUMENT,
      };

      const subscribeId = Date.now();
      socket.emit({
        id: subscribeId,
        method: SOCKET_METHODS.SUBSCRIBE,
        params: param,
      });

      socket.on(SOCKET_EVENTS.INSTRUMENT_CHANGED, (data) => {
        console.record('event', SOCKET_EVENTS.INSTRUMENT_CHANGED, data.type, data);

        const newInstrument = transformFuturesInstrument(data);
        queryClient.setQueryData(QUERY_KEYS.FUTURES.INSTRUMENT(chainId, instrumentAddr), newInstrument);
      });

      socket.on(SOCKET_EVENTS.MARKET_PAIR_INFO_CHANGED, (data) => {
        console.record('event', SOCKET_EVENTS.MARKET_PAIR_INFO_CHANGED, data);
        const newMarketInfo = transformMarketInfo(data, chainId);
        queryClient.setQueryData(QUERY_KEYS.FUTURES.PAIR_INFO(chainId, instrumentAddr, expiry), newMarketInfo);
      });

      return () => {
        socket.off(SOCKET_EVENTS.INSTRUMENT_CHANGED);
        socket.off(SOCKET_EVENTS.MARKET_PAIR_INFO_CHANGED);
        socket.emit({ id: subscribeId, method: SOCKET_METHODS.UNSUBSCRIBE, params: param });
      };
    },
    [chainId, expiry, instrumentAddr, socket],
    {
      wait: 200,
    },
  );
}

export function useFuturesOrderBookWithStepRatio(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  stepRatio: number | undefined,
) {
  const { data: orderBook } = useFetchFuturesOrderbook(chainId, instrumentAddr, expiry, stepRatio);
  return useMemo(() => {
    if (orderBook && stepRatio) {
      return orderBook[stepRatio] ?? { left: [], right: [] };
    }
    return { left: [], right: [] } as IFuturesOrderBook;
  }, [orderBook, stepRatio]);
}

export function useIsFetchedFuturesOrderBook(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  stepRatio: number | undefined,
) {
  const { isFetched } = useFetchFuturesOrderbook(chainId, instrumentAddr, expiry, stepRatio);

  return isFetched;
}

export function useFuturesDepthChartsData(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) {
  const { data: depth } = useFetchFuturesDepthChart(chainId, instrumentAddr, expiry);

  return useMemo(() => {
    if (depth) {
      const [left, right] = [depth.left, depth.right];
      const leftReduced = transformData(left);
      const rightReduced = transformData(right);
      return {
        ...depth,
        left: leftReduced,
        right: rightReduced,
      };
    }
  }, [depth]);
}

export function useIsFetchedFuturesDepthCharts(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) {
  const { isFetched } = useFetchFuturesDepthChart(chainId, instrumentAddr, expiry);

  return isFetched;
}

export function useFuturesFundingChartsData(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  chartDuration: FundingChartInterval | undefined,
) {
  const { data: funding } = useFetchFuturesFundingChart(chainId, instrumentAddr, expiry, chartDuration);

  return useMemo(() => {
    if (funding) {
      return funding;
    }
  }, [funding]);
}

export function useIsFetchedFuturesFundingCharts(
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  chartDuration: FundingChartInterval | undefined,
) {
  const { isFetched } = useFetchFuturesFundingChart(chainId, instrumentAddr, expiry, chartDuration);

  return isFetched;
}

export function useFuturesKlineChartsData({
  chainId,
  instrumentAddr,
  expiry,
  chartDuration,
  timeEnd,
}: {
  chainId?: number;
  instrumentAddr?: string;
  expiry?: number;
  chartDuration?: KlineInterval;
  timeEnd?: number;
}) {
  const { data: funding } = useFetchFuturesKlineChart({
    chainId,
    instrumentAddr,
    expiry,
    chartDuration,
    timeEnd,
  });
  console.log('🚀 ~ funding:', funding);

  return useMemo(() => {
    if (funding) {
      return funding;
    }
  }, [funding]);
}

export function useIsFetchedFuturesKlineCharts({
  chainId,
  instrumentAddr,
  expiry,
  chartDuration,
  timeEnd,
}: {
  chainId?: number;
  instrumentAddr?: string;
  expiry?: number;
  chartDuration?: KlineInterval;
  timeEnd?: number;
}) {
  const { isFetched } = useFetchFuturesKlineChart({
    chainId,
    instrumentAddr,
    expiry,
    chartDuration,
    timeEnd,
  });

  return isFetched;
}
